import './highlightedInput.less'
import { useReduxSelector } from '../../../../../../../hooks/useReduxSelector'
import { removeDuplicatesWithKeys } from '../../../../../../../utils'
import { TextArea, Tooltip, Typography } from '@lightbeamai/design-system'
import React, { useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import pSBC from 'shade-blend-color'

const DEFAULT_LINES = 5
interface Item {
  start: number
  end: number
  type: string
  value: string
}

export const HighlightedInput = ({
  value,
  onChange
}: {
  value: string
  onChange: (value: string) => void
}) => {
  const intl = useIntl()

  const wordRef = useRef<HTMLDivElement>(null)
  const highlightRef = useRef<HTMLDivElement>(null)

  const validationResult = useReduxSelector((state) => state.attributes.validationResult)

  const { patternMatches = {}, contextMatches = {} } = validationResult || {}
  const totalPatternMatches = Object.keys(patternMatches)?.length || 0
  const totalContextMatches = Object.keys(contextMatches)?.length || 0

  const flattenedPatternMatches =
    useMemo(() => {
      return (
        Object.keys(patternMatches).reduce((acc, key) => {
          return [
            ...acc,
            ...patternMatches[key].map((item) => ({
              start: item[0],
              end: item[1],
              value: key,
              type: 'Pattern'
            }))
          ] as Item[]
        }, [] as Item[]) || []
      )
    }, [patternMatches]) || []

  const flattenedContextMatches = useMemo(() => {
    return (
      Object.keys(contextMatches).reduce((acc, key) => {
        return [
          ...acc,
          ...contextMatches[key].map((item) => ({
            start: item[0],
            end: item[1],
            value: key,
            type: 'Context Words'
          }))
        ] as Item[]
      }, [] as Item[]) || []
    )
  }, [contextMatches])

  const allMatches = [...flattenedContextMatches, ...flattenedPatternMatches]

  const sortedHighlights = allMatches.sort((a, b) => a.start - b.start)

  const renderHighlightedText = () => {
    if (!validationResult) return value
    if (totalContextMatches === 0 && totalPatternMatches === 0) {
      return value
    }
    const result: React.ReactNode[] = []
    let lastIndex = 0

    const getHighlightInfo = (index: number) => {
      const matchesAtIndex = sortedHighlights.filter((h) => h.start <= index && h.end > index)
      if (matchesAtIndex.length === 0) return null

      const colors = matchesAtIndex.map((m) => (m.type === 'Pattern' ? '#723DFD' : '#2899E0'))
      const patterns = matchesAtIndex.map((m) => ({ value: m.value, type: m.type }))
      const uniquePatterns = removeDuplicatesWithKeys(patterns, 'value', 'type')
      const color = pSBC(0.5, ...colors)

      return {
        // backgroundColor: pSBC(0.5, color),
        backgroundColor: color,
        title: (
          <Typography color="white" variant="baseMinus2" as="div">
            {intl.formatMessage({ id: 'matchedBy' })}:
            {uniquePatterns.map((p) => (
              <>
                <br />
                <span key={p.value}>
                  {p.type}: {p.value}
                </span>
              </>
            ))}
          </Typography>
        )
      }
    }
    for (let i = 0; i < value.length; i++) {
      const highlightInfo = getHighlightInfo(i)
      if (highlightInfo) {
        if (i > lastIndex) {
          result.push(value.slice(lastIndex, i))
        }
        result.push(
          <Tooltip key={i} arrow title={highlightInfo.title} placement="top">
            <mark
              style={{
                backgroundColor: highlightInfo.backgroundColor,
                opacity: 0.5
              }}
            >
              {value[i]}
            </mark>
          </Tooltip>
        )
        lastIndex = i + 1
      }
    }

    if (lastIndex < value.length) {
      result.push(value.slice(lastIndex))
    }

    return result
  }

  const handleClick = (e) => {
    e.stopPropagation()
    e.preventDefault()
    handleCursorPosition(e)
    if (wordRef.current) {
      wordRef.current.focus()
    }
  }

  const handleScroll = (e) => {
    const scrollTop = e.target.scrollTop

    if (
      e.target === (wordRef.current as any)?.resizableTextArea?.textArea &&
      highlightRef.current
    ) {
      highlightRef.current.scrollTop = scrollTop
    }
  }

  const handleScroll2 = (e) => {
    const scrollTop = e.target.scrollTop

    if (e.target === highlightRef.current && wordRef.current) {
      ;(wordRef.current as any).resizableTextArea.textArea.scrollTop = scrollTop
    }
  }

  const handleCursorPosition = (e) => {
    const cursorPosition = e.target.selectionStart
    if (e.target === highlightRef.current) {
      ;(wordRef.current as any).resizableTextArea.textArea.setSelectionRange(
        cursorPosition,
        cursorPosition
      )
    }
  }

  useEffect(() => {
    if (validationResult && highlightRef.current) {
      highlightRef.current.scrollTop = 0
      ;(wordRef.current as any).resizableTextArea.textArea.scrollTop = 0
    }
  }, [validationResult])

  return (
    <div className="highlighted-input-wrapper">
      <TextArea
        rows={DEFAULT_LINES}
        value={value}
        className="highlight-textarea"
        onChange={(e) => onChange(e.target.value)}
        onScroll={handleScroll}
        placeholder={intl.formatMessage({
          id: 'attributeManagementSettings.customAttributes.detection.testInput.placeholder'
        })}
        ref={wordRef}
      ></TextArea>
      {validationResult && (
        <div
          onClick={handleClick}
          className="highlighted-words"
          ref={highlightRef}
          onScroll={handleScroll2}
        >
          {renderHighlightedText()}
        </div>
      )}
    </div>
  )
}
